import { ApiError, NetworkError } from '@dovefiniscono/api-client';
import {
  type LoginInput,
  type OAuthProvider,
  type RegisterInput,
  type User,
  userSchema,
} from '@dovefiniscono/shared';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api } from './api';
import { forgetLocalData } from './local-data';
import { forgetPush } from './push';

export type Session =
  | { status: 'loading'; user: null }
  /** reason: "manual" after "Esci", "expired" when the server dropped the session. */
  | { status: 'signedOut'; user: null; reason?: 'manual' | 'expired' }
  | { status: 'signedIn'; user: User };

export type SessionValue = Session & {
  providers: OAuthProvider[];
  /** Email and password on offer. Unknown until the server says so. */
  passwordSignIn: boolean | null;
  signIn(input: LoginInput): Promise<void>;
  signUp(input: RegisterInput): Promise<void>;
  signOut(): Promise<void>;
  /** The server no longer accepts the session (detected by sync). */
  expire(): void;
  updateUser(user: User): void;
};

const USER_KEY = 'dvf:user';
const PROVIDERS_KEY = 'dvf:providers';
const PASSWORD_KEY = 'dvf:password-sign-in';

function remember(key: string, value: unknown) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode: nothing to remember, the app still works online.
  }
}

function recall(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function cachedUser(): User | null {
  const parsed = userSchema.safeParse(recall(USER_KEY));
  return parsed.success ? parsed.data : null;
}

function cachedProviders(): OAuthProvider[] {
  const value = recall(PROVIDERS_KEY);
  return Array.isArray(value) ? (value as OAuthProvider[]) : [];
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  // Who was here last time opens at once, like any installed app: the server confirms it later.
  const [session, setSession] = useState<Session>(() => {
    const user = cachedUser();
    return user ? { status: 'signedIn', user } : { status: 'loading', user: null };
  });
  const [providers, setProviders] = useState<OAuthProvider[]>(cachedProviders);
  const [passwordSignIn, setPasswordSignIn] = useState<boolean | null>(() => {
    const known = recall(PASSWORD_KEY);
    return typeof known === 'boolean' ? known : null;
  });

  useEffect(() => {
    api
      .meta()
      .then((meta) => {
        setProviders(meta.authProviders);
        remember(PROVIDERS_KEY, meta.authProviders);
        setPasswordSignIn(meta.passwordSignIn);
        remember(PASSWORD_KEY, meta.passwordSignIn);
      })
      .catch(() => {});

    api.me
      .get()
      .then(({ user }) => {
        remember(USER_KEY, user);
        setSession({ status: 'signedIn', user });
      })
      .catch((error: unknown) => {
        // Offline: open with the last known profile; local data is in IndexedDB.
        const cached = error instanceof NetworkError ? cachedUser() : null;
        if (cached) return setSession({ status: 'signedIn', user: cached });
        if (error instanceof ApiError && error.status === 401) {
          remember(USER_KEY, null);
          void forgetPush('gone');
        }
        setSession({ status: 'signedOut', user: null });
      });
  }, []);

  const current = useRef(session);
  current.current = session;

  const start = useCallback((user: User) => {
    remember(USER_KEY, user);
    setSession({ status: 'signedIn', user });
  }, []);

  const end = useCallback((reason: 'manual' | 'expired') => {
    const user = current.current.user;
    if (user) void forgetLocalData(user.id).catch(() => {});
    remember(USER_KEY, null);
    setSession({ status: 'signedOut', user: null, reason });
  }, []);

  // Stable identities: screens and the data layer depend on them.
  const actions = useMemo(
    () => ({
      signIn: async (input: LoginInput) => start((await api.auth.login(input)).user),
      signUp: async (input: RegisterInput) => start((await api.auth.register(input)).user),
      signOut: async () => {
        await forgetPush('open');
        await api.auth.logout().catch(() => {});
        end('manual');
      },
      expire: () => {
        void forgetPush('gone');
        end('expired');
      },
      updateUser: (user: User) => start(user),
    }),
    [start, end],
  );

  const value = useMemo<SessionValue>(
    () => ({ ...session, providers, passwordSignIn, ...actions }),
    [session, providers, passwordSignIn, actions],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/** For screens that only render with a signed-in user. */
export function useUser(): User {
  const { user } = useSession();
  if (!user) throw new Error('useUser needs a signed-in session');
  return user;
}
